import { useEffect, useState, useCallback } from "react";
import api, { apiError, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ShieldCheck, Users, UserCheck, ClipboardCheck, Eye, Award, Building2, MapPin,
  FileDown, Filter, ChevronDown, GraduationCap, Loader2,
} from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, hint, icon: Icon, tone = "text-primary", testid }) {
  return (
    <Card data-testid={testid} className="p-3 bg-card border-border">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono leading-tight">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-heading font-bold">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </Card>
  );
}

export default function CapacitadosConsolidado() {
  const [f, setF] = useState({ turma_id: "", unidade: "", instituicao: "", inicio: "", fim: "" });
  const [geral, setGeral] = useState(null);
  const [militares, setMilitares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aberto, setAberto] = useState({});

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [f]);

  const load = useCallback(() => {
    setLoading(true);
    const q = qs();
    Promise.all([
      api.get(`/capacitados/consolidado/geral?${q}`),
      api.get(`/capacitados/consolidado/militares?${q}`),
    ]).then(([g, m]) => { setGeral(g.data); setMilitares(m.data.militares); })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [qs]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportar = (tipo, fmt) => {
    const p = new URLSearchParams(qs());
    p.set("tipo", tipo);
    downloadFile(`/capacitados/consolidado/relacao/export/${fmt}?${p.toString()}`, `MÉTRON_Consolidado_${tipo}.${fmt}`)
      .catch(() => toast.error("Falha ao exportar"));
  };

  const limpar = () => setF({ turma_id: "", unidade: "", instituicao: "", inicio: "", fim: "" });

  const ind = geral?.indicadores;

  return (
    <div data-testid="capacitados-consolidado">
      <PageHeader title="Capacitados GNA" subtitle="Visão consolidada de todas as instruções de Primeiro Interventor" />

      {/* Filtros */}
      <Card className="p-4 bg-card border-border mb-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium"><Filter className="h-4 w-4 text-primary" />Filtros</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Instrução / Turma</label>
            <Select value={f.turma_id || "todas"} onValueChange={(v) => setF({ ...f, turma_id: v === "todas" ? "" : v })}>
              <SelectTrigger data-testid="filtro-turma" className="h-9"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="todas">Todas</SelectItem>
                {(geral?.turmas || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Unidade</label>
            <Input data-testid="filtro-unidade" value={f.unidade} onChange={(e) => setF({ ...f, unidade: e.target.value })} placeholder="Ex.: 1º BPM" className="h-9" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Instituição</label>
            <Input data-testid="filtro-instituicao" value={f.instituicao} onChange={(e) => setF({ ...f, instituicao: e.target.value })} placeholder="Ex.: PMERJ" className="h-9" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Início</label>
            <Input type="date" data-testid="filtro-inicio" value={f.inicio} onChange={(e) => setF({ ...f, inicio: e.target.value })} className="h-9" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Fim</label>
            <Input type="date" data-testid="filtro-fim" value={f.fim} onChange={(e) => setF({ ...f, fim: e.target.value })} className="h-9" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" data-testid="aplicar-filtros" onClick={load} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Filter className="h-4 w-4 mr-1.5" />}Aplicar</Button>
          <Button size="sm" variant="outline" data-testid="limpar-filtros" onClick={() => { limpar(); setTimeout(load, 0); }}>Limpar</Button>
        </div>
      </Card>

      {ind && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
            <Stat testid="c-instrucoes" label="Instruções realizadas" value={ind.instrucoes} hint={`${ind.instrucoes_com_participantes} com presença`} icon={ShieldCheck} tone="text-primary" />
            <Stat testid="c-receberam" label="Militares que receberam" value={ind.militares_receberam} hint="participações (militar × turma)" icon={Users} tone="text-amber-400" />
            <Stat testid="c-unicos" label="Militares únicos capacitados" value={ind.militares_unicos} icon={UserCheck} tone="text-emerald-400" />
            <Stat testid="c-avaliados" label="Avaliados (únicos)" value={ind.avaliados_unicos} icon={ClipboardCheck} tone="text-primary" />
            <Stat testid="c-acompanharam" label="Apenas acompanharam" value={ind.apenas_acompanharam_unicos} icon={Eye} tone="text-sky-400" />
            <Stat testid="c-aptos" label="Aptos (únicos)" value={ind.aptos_unicos} icon={Award} tone="text-emerald-400" />
          </div>

          {/* Exportações consolidadas */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[["participantes", "Relação de Participantes"], ["aptos", "Relação de Aptos"]].map(([tipo, label]) => (
              <DropdownMenu key={tipo}>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="secondary" data-testid={`exp-cons-${tipo}`}><FileDown className="h-3.5 w-3.5 mr-1.5" />{label}</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-popover border-border">
                  <DropdownMenuItem data-testid={`exp-cons-${tipo}-docx`} onClick={() => exportar(tipo, "docx")}>Word (.docx)</DropdownMenuItem>
                  <DropdownMenuItem data-testid={`exp-cons-${tipo}-xlsx`} onClick={() => exportar(tipo, "xlsx")}>Excel (.xlsx)</DropdownMenuItem>
                  <DropdownMenuItem data-testid={`exp-cons-${tipo}-pdf`} onClick={() => exportar(tipo, "pdf")}>PDF (.pdf)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <TabelaAgg titulo="Por instituição" icon={Building2} itens={geral.por_instituicao} testid="tab-instituicao" />
            <TabelaAgg titulo="Militares por unidade" icon={MapPin} itens={geral.por_unidade} testid="tab-unidade" />
          </div>

          {/* Por instrução */}
          <SectionTitle icon={ShieldCheck}>Instruções ({geral.por_turma.length})</SectionTitle>
          <Card className="bg-card border-border divide-y divide-border mb-6">
            {geral.por_turma.length ? geral.por_turma.map((t) => (
              <div key={t.turma_id} data-testid={`cons-turma-${t.turma_id}`} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.nome}</div>
                  <div className="text-xs text-muted-foreground font-mono">{t.data_instrucao}</div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">{t.participantes} presentes</Badge>
                  <Badge className="bg-primary/15 text-primary border-primary/30">{t.avaliados} aval.</Badge>
                  <Badge className="bg-secondary text-secondary-foreground border-border">{t.aptos} aptos</Badge>
                </div>
              </div>
            )) : <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma instrução com presença no filtro.</div>}
          </Card>

          {/* Histórico por militar */}
          <SectionTitle icon={GraduationCap}>Histórico por militar ({militares.length})</SectionTitle>
          <Card className="bg-card border-border divide-y divide-border">
            {militares.length ? militares.map((m) => (
              <div key={m.aluno_id} data-testid={`cons-militar-${m.aluno_id}`}>
                <button onClick={() => setAberto((s) => ({ ...s, [m.aluno_id]: !s[m.aluno_id] }))}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-secondary/40 text-left">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{m.grau_nome && m.grau_nome !== "Não se aplica" ? m.grau_nome + " " : ""}{m.nome} {m.nome_guerra && <span className="text-xs text-primary font-mono">"{m.nome_guerra}"</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{[m.unidade_nome, m.instituicao_nome].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">{m.total_participacoes} instrução(ões)</Badge>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${aberto[m.aluno_id] ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {aberto[m.aluno_id] && (
                  <div className="px-4 pb-3 space-y-1">
                    {m.participacoes.map((p, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs bg-secondary/30 rounded px-2.5 py-1.5">
                        <span className="truncate">{p.turma_nome} · <span className="font-mono text-muted-foreground">{p.data_instrucao}</span></span>
                        <span className="flex gap-1.5 shrink-0">
                          <Badge className={p.avaliado ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-sky-500/20 text-sky-300 border-sky-500/40"}>{p.avaliado ? "Avaliado" : "Acompanhou"}</Badge>
                          <Badge className="bg-secondary text-secondary-foreground border-border">{p.resultado_label}</Badge>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )) : <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhum militar com presença confirmada no filtro.</div>}
          </Card>
        </>
      )}
    </div>
  );
}

function TabelaAgg({ titulo, icon: Icon, itens, testid }) {
  return (
    <Card data-testid={testid} className="bg-card border-border p-4">
      <div className="flex items-center gap-2 mb-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" />{titulo}</div>
      <div className="divide-y divide-border">
        {itens.length ? itens.map((it, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 text-sm">
            <span className="truncate">{it.nome}</span>
            <Badge className="bg-secondary text-secondary-foreground border-border shrink-0">{it.militares}</Badge>
          </div>
        )) : <div className="py-4 text-center text-xs text-muted-foreground">Sem dados.</div>}
      </div>
    </Card>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-5 w-5 text-primary" />
      <h2 className="font-heading text-xl font-bold uppercase tracking-tight">{children}</h2>
    </div>
  );
}
